import PropTypes from 'prop-types';
import React from 'react';
import { connect as connectR } from 'react-redux';
import { Button, ButtonGroup, Col, FormControl, Row } from 'react-bootstrap';
import _ from 'lodash';
import ValidatorFormGroup from '../../main/validator-form-group';
import translate from '../../translate';
import {
    APPLIANCE_TYPE,
    CONTROL_TYPE,
    PARTNER_MANAGER_CONTROL,
} from '../../../util/enums';
import FieldContractualType from '../../realms/field-contractual-type';
import { resetApplianceRequestStates } from '../../../store/applianceSlice';
import { MaybeLoadContractDetails } from '../../../store/userDetailsSlice';
import { RequestStatus } from '../../../store/network/managedRequest';
import { LoadPartnerManagerDetails } from '../../../store/partnerManagerSlice';
import { LoadAccountAreaApplianceDefaults } from '../../../store/accountingAreaDefaultsSlice';
import { PbRequest } from '../../../store/pbRequest';
import { Combobox } from 'react-widgets';
import memoizeOne from 'memoize-one';
import Spinner from '../../util/spinner';
import { LoadConnectedSwitchSerialNumbers } from '../../../store/serialNumbersSlice';
import RefreshButton from '../../main/refresh-button';

const DEFAULT_WASHER_MODEL_GROUP = '';
const DEFAULT_DRYER_MODEL_GROUP = '';

const FIELD_CONTRACTUAL_TYPE = 'contractual_type';
const FIELD_MANUFACTURING_BATCH = 'manufacturing_batch';
const FIELD_MODEL_GROUP = 'model_group';
const FIELD_PRODUCT_CODE = 'product_code';
const PRODUCT_CODE_NONE = 'NO_PRODUCT_CODE';

const VALIDATORS = {
    local_name: { isValid: (value) => value.length > 2 && value.length < 63 },
    appliance_type: {
        isValid: (value) =>
            value === APPLIANCE_TYPE.washer || value === APPLIANCE_TYPE.dryer,
    },
    short_name: {
        isValid: (value, appliance_type) => {
            const name_correct = /^\s*(W|T)\d{1,2}.*$/.test(value);
            const type_correct =
                !appliance_type ||
                (appliance_type === APPLIANCE_TYPE.washer &&
                    value[0] === 'W') ||
                (appliance_type === APPLIANCE_TYPE.dryer && value[0] === 'T');
            return name_correct && type_correct;
        },
    },
    control_type: { isValid: () => true },
    serial_number: {
        isValid: (value) => /^[a-zA-Z0-9]{0,20}$/.test(value),
    },
    [FIELD_CONTRACTUAL_TYPE]: { isValid: () => true },
    [FIELD_PRODUCT_CODE]: {
        isValid: (value, state, config, isExternalManager) => {
            const actualType = state.appliance_type;
            const expectedConfig = config.models?.find(
                (it) => it.product_code === value
            );
            const expectedType = expectedConfig?.type;

            if (isExternalManager) {
                return true;
            } else {
                return (
                    !actualType || !expectedType || actualType === expectedType
                );
            }
        },
    },
    [FIELD_MANUFACTURING_BATCH]: { isValid: () => true },
    [FIELD_MODEL_GROUP]: {
        isValid: (value, state, config, isExternalManager) => {
            const expectedConfig =
                (state.appliance_type === APPLIANCE_TYPE.washer
                    ? config.washer_appliance_model_groups
                    : config.dryer_appliance_model_groups) || [];

            if (isExternalManager || !value) {
                return true;
            } else {
                return expectedConfig.includes(value);
            }
        },
    },
};

class ApplianceBasicForm extends React.Component {
    static propTypes = {
        info: PropTypes.shape({
            local_name: PropTypes.string,
            appliance_type: PropTypes.string,
            control_type: PropTypes.string,
            short_name: PropTypes.string,
            serial_number: PropTypes.string,
        }),
        handleChange: PropTypes.func.isRequired,
        location_id: PropTypes.number.isRequired,
        forceValidation: PropTypes.bool,
        disabled: PropTypes.bool,
        strings: PropTypes.object,
        applianceStore: PropTypes.shape({
            appliancesByLaundryRoom: PropTypes.shape({}),
        }).isRequired,
        createApplianceRequest: PropTypes.shape({
            error: PropTypes.arrayOf(PropTypes.string),
        }),
        updateApplianceRequest: PropTypes.shape({
            error: PropTypes.arrayOf(PropTypes.string),
        }),
        deleteApplianceRequest: PropTypes.shape({
            error: PropTypes.arrayOf(PropTypes.string),
        }),
        serialNumbers: PropTypes.arrayOf(
            PropTypes.shape({
                number: PropTypes.string,
                type: PropTypes.string,
                connected: PropTypes.bool,
            })
        ).isRequired,
        serialNumbersStatus: PropTypes.oneOf(Object.values(RequestStatus))
            .isRequired,
        resetApplianceRequestStates: PropTypes.func.isRequired,
        locationsByMansionsById: PropTypes.shape({}).isRequired,
        applianceDefaults: PropTypes.shape({
            models: PropTypes.arrayOf(
                PropTypes.shape({
                    product_code: PropTypes.string,
                    type: PropTypes.string,
                    group: PropTypes.string,
                    control_type: PropTypes.string,
                    default_name: PropTypes.string,
                    default_manufacturing_batch: PropTypes.string,
                })
            ),
            washer_appliance_model_groups: PropTypes.arrayOf(PropTypes.string),
            dryer_appliance_model_groups: PropTypes.arrayOf(PropTypes.string),
        }),
        partnerManagerDetailsStatus: PropTypes.oneOf(
            Object.values(RequestStatus)
        ).isRequired,
        partnerManagerDetails: PropTypes.shape({
            control: PropTypes.oneOf(Object.values(PARTNER_MANAGER_CONTROL)),
        }),
        contractDetails: PropTypes.shape({}).isRequired,
        isAdminAccountManager: PropTypes.bool.isRequired,
        MaybeLoadContractDetails: PropTypes.func.isRequired,
        LoadAccountAreaApplianceDefaults: PropTypes.func.isRequired,
        LoadPartnerManagerDetails: PropTypes.func.isRequired,
        LoadConnectedSwitchSerialNumbers: PropTypes.func.isRequired,
    };

    state = _.extend(
        {
            id: null,
            state: 'OFFLINE',
            appliance_type: APPLIANCE_TYPE.washer,
            local_name: '',
            short_name: '',
            control_type: CONTROL_TYPE.power,
            serial_number: '',
            [FIELD_CONTRACTUAL_TYPE]: null,
            [FIELD_MANUFACTURING_BATCH]: '',
            [FIELD_MODEL_GROUP]: '',
            [FIELD_PRODUCT_CODE]: '',
            _dirty: {},
            _valid: true,
        },
        this.props.info
    );

    componentDidMount() {
        this.props.MaybeLoadContractDetails();
        this.props.LoadAccountAreaApplianceDefaults();
        this.props.LoadPartnerManagerDetails();
        const hasSerialNumber = this.props.info?.serial_number;

        let newState = {};

        if (!hasSerialNumber && this.props.serialNumbers.length === 1) {
            const preselectedSerialNumber = this.props.serialNumbers[0].number;
            newState = _.extend(newState, {
                serial_number: preselectedSerialNumber,
            });
        }

        if (Object.keys(newState).length > 0) {
            this.setState(newState, this._persistState);
        } else {
            this._persistState();
        }
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        let values = {};
        let forceUpdate = false;

        const outsideChangeOrFirstTime =
            this.props.info && !_.isEqual(prevProps.info, this.props.info);
        const resetOnDisable =
            this.props.info && this.props.disabled && !prevProps.disabled;
        if (outsideChangeOrFirstTime || resetOnDisable) {
            values = {...values, ...this.props.info};
        }

        const hasId = this.props.info?.id;
        if (
            !hasId &&
            this.props.applianceDefaults &&
            this.state.product_code === ''
        ) {
            const templateValues = this.getDefaultTemplateValues(this.props);

            const willChange = !_.isEqual(
                _.pick(this.state, Object.keys(templateValues)),
                templateValues
            );

            if (willChange) {
                values = {...values, ...templateValues};
            }
        }

        const selectedItem = this.state[FIELD_CONTRACTUAL_TYPE];
        if (!selectedItem) {
            const accountingAreaType = this.getAccountingAreaType();
            if (accountingAreaType) {
                values = {...values, [FIELD_CONTRACTUAL_TYPE]: accountingAreaType };
            }
        }

        if (this.props.applianceDefaults && !prevProps.applianceDefaults) {
            forceUpdate = true;
        }

        if (Object.keys(values).length > 0 || forceUpdate) {
            this.setState(values, this._persistState);
        }
    }

    getDefaultTemplateValues = (props) => {
        const templateModelGroup =
            this.state.appliance_type === APPLIANCE_TYPE.washer
                ? DEFAULT_WASHER_MODEL_GROUP
                : DEFAULT_DRYER_MODEL_GROUP;
        const chosenTemplate = this.getApplianceModels(props).find(
            (it) => it.group === templateModelGroup
        );
        return this.maybeApplyTemplate(chosenTemplate);
    };

    getAccountingAreaType = () => {
        return (
            this.props.contractDetails &&
            this.props.contractDetails[FIELD_CONTRACTUAL_TYPE]
        );
    };

    getApplianceModels = (props) => {
        const propsToUse = props || this.props;
        if (!propsToUse.applianceDefaults) {
            return [];
        }
        return propsToUse.applianceDefaults.models ?? [];
    };

    _isUniqueApplianceShortName = () => {
        const locationId = this.props.location_id;
        const appliancesInLaundryRoom =
            this.props.applianceStore.appliancesByLaundryRoom[locationId] || {};
        const applianceWithName = Object.keys(appliancesInLaundryRoom)
            .map((key) => appliancesInLaundryRoom[key])
            .find(
                (it) =>
                    it.short_name === this.state.short_name &&
                    String(it.id) !== String(this.state.id)
            );
        return !applianceWithName;
    };

    _isValidShortName = () => {
        return (
            VALIDATORS.short_name.isValid(
                this.state.short_name,
                this.state.appliance_type
            ) && this._isUniqueApplianceShortName()
        );
    };

    _isValidField = (testState, fieldName) => {
        if (!VALIDATORS.hasOwnProperty(fieldName)) {
            return true;
        }
        switch (fieldName) {
            case FIELD_PRODUCT_CODE:
            case FIELD_MODEL_GROUP:
                return VALIDATORS[fieldName].isValid(
                    testState[fieldName],
                    testState,
                    this.props.applianceDefaults || {},
                    this.isExternalManager()
                );
            case 'short_name':
                return VALIDATORS[fieldName].isValid(
                    testState[fieldName],
                    testState.appliance_type
                );
            default:
                return VALIDATORS[fieldName].isValid(testState[fieldName]);
        }
    };

    _persistState = () => {
        const new_state = {};
        const fieldsExternal = [
            'state',
            FIELD_CONTRACTUAL_TYPE,
            'appliance_type',
            'local_name',
            'short_name',
            'control_type',
            'serial_number',
        ];
        const fieldsInternal = fieldsExternal.concat([
            FIELD_PRODUCT_CODE,
            FIELD_MANUFACTURING_BATCH,
            FIELD_MODEL_GROUP,
        ]);
        _.each(
            this.isExternalManager() ? fieldsExternal : fieldsInternal,
            (fieldName) => {
                new_state[fieldName] = this.state[fieldName];
            }
        );
        new_state._valid = Object.keys(VALIDATORS).reduce(
            (formValid, fieldName) =>
                formValid && this._isValidField(new_state, fieldName),
            true
        );
        this.props.handleChange(new_state);
    };

    _markDirty = (key) => {
        const newDirt = {};
        newDirt[key] = true;
        const newState = _.extend({}, this.state, {
            _dirty: _.extend({}, this.state._dirty, newDirt),
        });
        this.setState(newState);
    };

    _handleFormChange = (key, value) => {
        this._markDirty(key);
        const newSt = _.set({}, key, value);
        newSt[key] = value;
        if (key === 'appliance_type') {
            newSt.short_name =
                (value === APPLIANCE_TYPE.washer ? 'W' : 'T') +
                this.state.short_name.substr(1);
            if (
                value === APPLIANCE_TYPE.dryer &&
                this.state.control_type === CONTROL_TYPE.powerConstant
            ) {
                newSt.control_type = CONTROL_TYPE.power;
            }
            const templateModelGroup =
                value === APPLIANCE_TYPE.washer
                    ? DEFAULT_WASHER_MODEL_GROUP
                    : DEFAULT_DRYER_MODEL_GROUP;
            const chosenTemplate = this.getApplianceModels().find(
                (it) => it.group === templateModelGroup
            );
            _.assign(newSt, this.maybeApplyTemplate(chosenTemplate));
        } else if (key === 'is_professional_appliance') {
            newSt.control_type = value
                ? CONTROL_TYPE.paymentSignal
                : CONTROL_TYPE.power;
        } else if (key === 'has_constant_power_supply') {
            newSt.control_type = value
                ? CONTROL_TYPE.powerConstant
                : CONTROL_TYPE.power;
        } else if (key === 'serial_number') {
            this.props.resetApplianceRequestStates();
        } else if (key === FIELD_PRODUCT_CODE) {
            if (value === PRODUCT_CODE_NONE) {
                newSt[FIELD_PRODUCT_CODE] = '';
            }
            const chosenTemplate = this.getApplianceModels().find(
                (it) => it[FIELD_PRODUCT_CODE] === value
            );
            const templateFields = this.maybeApplyTemplate(chosenTemplate);
            _.assign(newSt, templateFields);
        }

        if (!newSt[FIELD_CONTRACTUAL_TYPE]) {
            const accountingAreaType = this.getAccountingAreaType();
            if (accountingAreaType) {
                newSt[FIELD_CONTRACTUAL_TYPE] = accountingAreaType;
            }
        }
        this.setState(newSt, this._persistState);
    };

    maybeApplyTemplate = (chosenTemplate) => {
        if (!chosenTemplate) {
            return {
                [FIELD_PRODUCT_CODE]: '',
                [FIELD_MANUFACTURING_BATCH]: '',
            };
        }
        const result = {
            [FIELD_PRODUCT_CODE]: chosenTemplate.product_code,
        };
        if (chosenTemplate.group) {
            result[FIELD_MODEL_GROUP] = chosenTemplate.group;
        }
        if (chosenTemplate.default_manufacturing_batch) {
            result[FIELD_MANUFACTURING_BATCH] =
                chosenTemplate.default_manufacturing_batch;
        } else {
            result[FIELD_MANUFACTURING_BATCH] = '';
        }
        if (chosenTemplate.default_name) {
            result.local_name = chosenTemplate.default_name;
        }
        if (chosenTemplate.control_type) {
            result.control_type = chosenTemplate.control_type;
        }
        return result;
    };

    _getErrorMessage = () => {
        const error =
            this.props.updateApplianceRequest.error ||
            this.props.createApplianceRequest.error ||
            this.props.deleteApplianceRequest.error;
        if (error) {
            return (
                <div className="alert alert-danger">
                    {this.props.strings.errorTags[error]}
                </div>
            );
        } else {
            return null;
        }
    };

    isExternalManager = () => {
        return (
            this.props.partnerManagerDetails?.control ===
            PARTNER_MANAGER_CONTROL.NORMAL
        );
    };

    renderSerialNumberInput = () => {
        const serialNumbersLoading =
            this.props.serialNumbersStatus === RequestStatus.PENDING;

        return (
            <ValidatorFormGroup
                label={this.props.strings.serial_number}
                errorLabel={this.props.strings.serial_number_input_error}
                hideErrorLabel={
                    !(
                        this.state._dirty.serial_number ||
                        this.props.forceValidation
                    ) ||
                    VALIDATORS.serial_number.isValid(this.state.serial_number)
                }
            >
                <div style={{ display: 'flex', gap: '10px' }}>
                    <div style={{ flex: 1 }}>
                        <Combobox
                            data={this.props.serialNumbers}
                            onChange={(value) =>
                                this._handleFormChange(
                                    'serial_number',
                                    typeof value === 'object'
                                        ? value.number
                                        : value
                                )
                            }
                            itemComponent={(
                                { item } //NOSONAR(javascript:S6478)
                            ) => (
                                <span>
                                    {item.number}
                                    {item?.connected
                                        ? ` ${this.props.strings.connected}`
                                        : ''}
                                </span>
                            )}
                            valueField="number"
                            textField="number"
                            value={this.state.serial_number || ''}
                            busy={serialNumbersLoading}
                            busySpinner={<Spinner />}
                            disabled={this.props.disabled}
                            messages={{
                                emptyList:
                                    this.props.strings.serial_number_input_hint,
                            }}
                            data-testid="serial-number-input"
                        />
                    </div>
                    {!this.props.disabled && (
                        <RefreshButton
                            onClick={this.handleRefreshSerialNumbers}
                            showLabel={false}
                            loading={serialNumbersLoading}
                        />
                    )}
                </div>
            </ValidatorFormGroup>
        );
    };

    handleRefreshSerialNumbers = () => {
        this.props.LoadConnectedSwitchSerialNumbers(
            this.props.location_id,
            true
        );
    };

    renderPowerControlSelect = () => {
        return (
            <div>
                <ValidatorFormGroup label={this.props.strings.controlType}>
                    <p className="controlTypeGroupTitle">
                        {this.props.strings.signalType}
                    </p>
                    {this.renderPowerControlOption(
                        CONTROL_TYPE.paymentSignalDuringLoading
                    )}
                    {this.renderPowerControlOption(
                        CONTROL_TYPE.paymentSignalFullDuration
                    )}
                    {this.renderPowerControlOption(CONTROL_TYPE.paymentSignal)}
                    <p className="controlTypeGroupTitle">
                        {this.props.strings.powerType}
                    </p>
                    {this.renderPowerControlOption(CONTROL_TYPE.power)}
                    {this.renderPowerControlOption(CONTROL_TYPE.powerConstant)}
                </ValidatorFormGroup>
            </div>
        );
    };

    renderPowerControlOption = (powerControl) => {
        const texts = this.props.strings.controlTypes[powerControl];
        return (
            <div key={powerControl} className="controlTypeOption">
                <input
                    type="radio"
                    id={powerControl}
                    name="control_type"
                    value={powerControl}
                    disabled={this.props.disabled}
                    checked={this.state.control_type === powerControl}
                    onChange={(event) =>
                        this._handleFormChange(
                            'control_type',
                            event.target.value
                        )
                    }
                />
                <label className="controlTypeLabel" htmlFor={powerControl}>
                    {texts.title}
                </label>
                <div className="controlTypeTooltip">
                    <span className="controlTypeTooltipIcon">?</span>
                    <div className="controlTypeTooltipText">
                        <ul>
                            {_.map(texts.descriptions, (it, idx) => (
                                <li key={idx}>{it}</li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>
        );
    };

    renderDescriptionBox = () => {
        return (
            <ValidatorFormGroup
                label={this.props.strings.local_name}
                hideErrorLabel={
                    !(
                        this.state._dirty.local_name ||
                        this.props.forceValidation
                    ) || VALIDATORS.local_name.isValid(this.state.local_name)
                }
                errorLabel={this.props.strings.local_name_error}
            >
                <FormControl
                    type="text"
                    className="input-text-color"
                    value={this.state.local_name}
                    name="local_name"
                    onChange={(event) => {
                        this._handleFormChange(
                            'local_name',
                            event.target.value
                        );
                    }}
                    required
                    data-testid="description-input"
                    disabled={this.props.disabled}
                />
            </ValidatorFormGroup>
        );
    };

    renderDryerWashingMachineSelect = () => {
        return (
            <ValidatorFormGroup
                label={this.props.strings.appliance_type}
                hideErrorLabel={
                    !(
                        this.state._dirty.appliance_type ||
                        this.props.forceValidation
                    ) ||
                    VALIDATORS.appliance_type.isValid(this.state.appliance_type)
                }
                errorLabel={this.props.strings.appliance_type_error}
            >
                <ButtonGroup className="radio-tabs">
                    <Button
                        active={
                            this.state.appliance_type === APPLIANCE_TYPE.washer
                        }
                        onClick={() =>
                            this._handleFormChange(
                                'appliance_type',
                                APPLIANCE_TYPE.washer
                            )
                        }
                        disabled={this.props.disabled}
                        className="btn-primary"
                        data-testid="appliance-type-washer"
                    >
                        {this.props.strings.type_w}
                    </Button>
                    <Button
                        active={
                            this.state.appliance_type === APPLIANCE_TYPE.dryer
                        }
                        onClick={() =>
                            this._handleFormChange(
                                'appliance_type',
                                APPLIANCE_TYPE.dryer
                            )
                        }
                        disabled={this.props.disabled}
                        className="btn-primary"
                        data-testid="appliance-type-dryer"
                    >
                        {this.props.strings.type_d}
                    </Button>
                </ButtonGroup>
            </ValidatorFormGroup>
        );
    };

    renderDryerWashingMachineInfo = () => {
        let shortNameError = this.props.strings.short_name_error;
        if (!this._isUniqueApplianceShortName()) {
            shortNameError = this.props.strings.short_name_duplicate_error;
        }

        return (
            <Row>
                <Col xs={8} sm={8} md={8} lg={8}>
                    {this.renderDryerWashingMachineSelect()}
                </Col>
                <Col xs={4} sm={4} md={4} lg={4}>
                    <ValidatorFormGroup
                        label={this.props.strings.short_name}
                        hideErrorLabel={
                            !(
                                this.state._dirty.short_name ||
                                this.props.forceValidation
                            ) || this._isValidShortName()
                        }
                        errorLabel={shortNameError}
                    >
                        <select
                            className="form-control"
                            name="short_name"
                            onChange={(event) => {
                                this._handleFormChange(
                                    'short_name',
                                    event.target.value
                                );
                            }}
                            value={this.state.short_name}
                            disabled={this.props.disabled}
                            data-testid="appliance-short-name-select"
                            required
                        >
                            <option key={0} value={''}>
                                {this.props.strings.short_name_default}
                            </option>
                            {_.map(_.range(1, 21), (id) => {
                                const type =
                                    this.state.appliance_type ===
                                    APPLIANCE_TYPE.washer
                                        ? 'W'
                                        : 'T';
                                return (
                                    <option key={type + id} value={type + id}>
                                        {this.props.strings[
                                            `type${type}Label`
                                        ] + id}
                                    </option>
                                );
                            })}
                        </select>
                    </ValidatorFormGroup>
                </Col>
            </Row>
        );
    };

    renderModelInformation = () => {
        const applianceModels = this.getApplianceModels().filter(
            (it) => it.type === this.state.appliance_type
        );
        const isUnknownVib =
            this.state[FIELD_PRODUCT_CODE] === PRODUCT_CODE_NONE ||
            !this.state[FIELD_PRODUCT_CODE];
        const hasCurrentVib =
            isUnknownVib ||
            applianceModels.find(
                (it) => it.product_code === this.state[FIELD_PRODUCT_CODE]
            );

        return (
            <Row>
                <Col xs={8} sm={8} md={8} lg={8}>
                    <ValidatorFormGroup
                        label={this.props.strings.inputVib}
                        hideErrorLabel={
                            !this.state._dirty[FIELD_PRODUCT_CODE] ||
                            this._isValidField(this.state, FIELD_PRODUCT_CODE)
                        }
                        errorLabel={this.props.strings.inputVibBadMatch}
                    >
                        <select
                            className={'form-control'}
                            name={FIELD_PRODUCT_CODE}
                            value={
                                this.state[FIELD_PRODUCT_CODE] ||
                                PRODUCT_CODE_NONE
                            }
                            onChange={(event) =>
                                this._handleFormChange(
                                    FIELD_PRODUCT_CODE,
                                    event.target.value
                                )
                            }
                            disabled={this.props.disabled}
                            data-testid="article-number-select"
                        >
                            {!hasCurrentVib && (
                                <option
                                    value={this.state[FIELD_PRODUCT_CODE]}
                                    key={this.state[FIELD_PRODUCT_CODE]}
                                >
                                    {this.state[FIELD_PRODUCT_CODE]}
                                </option>
                            )}
                            {applianceModels.map((it) => (
                                <option
                                    value={it.product_code}
                                    key={it.product_code}
                                >
                                    {it.product_code}
                                </option>
                            ))}
                            <option value={PRODUCT_CODE_NONE}>
                                {this.props.strings.optionUnknownVib}
                            </option>
                        </select>
                    </ValidatorFormGroup>
                </Col>
                <Col xs={4} sm={4} md={4} lg={4}>
                    <ValidatorFormGroup
                        label={this.props.strings.inputManufacturingBatch}
                    >
                        <input
                            className={'input-text-color form-control'}
                            type={'text'}
                            value={this.state[FIELD_MANUFACTURING_BATCH]}
                            name={FIELD_MANUFACTURING_BATCH}
                            disabled={this.props.disabled}
                            onChange={(event) =>
                                this._handleFormChange(
                                    FIELD_MANUFACTURING_BATCH,
                                    event.target.value
                                )
                            }
                        />
                    </ValidatorFormGroup>
                </Col>
            </Row>
        );
    };

    renderModelGroupDropdown = () => {
        const applianceModels = this.getApplianceModels();
        const chosenTemplate = applianceModels.find(
            (it) => it.product_code === this.state[FIELD_PRODUCT_CODE]
        );
        const accessor =
            this.state.appliance_type === APPLIANCE_TYPE.washer
                ? 'washer_appliance_model_groups'
                : 'dryer_appliance_model_groups';
        const standardGroups = this.props.applianceDefaults?.[accessor] || [];
        const modelGroups = chosenTemplate
            ? [chosenTemplate.group]
            : standardGroups;

        const hasSelectedItem = modelGroups.includes(
            this.state[FIELD_MODEL_GROUP]
        );

        function renderOption(modelGroup) {
            return (
                <option key={modelGroup} value={modelGroup}>
                    {modelGroup}
                </option>
            );
        }

        return (
            <Row>
                <Col xs={8} sm={8} md={8} lg={8}>
                    <ValidatorFormGroup
                        label={this.props.strings.inputModelGroup}
                        hideErrorLabel={this._isValidField(
                            this.state,
                            FIELD_MODEL_GROUP
                        )}
                        errorLabel={this.props.strings.inputModelGroupBadMatch}
                    >
                        <select
                            disabled={!!chosenTemplate || this.props.disabled}
                            className={'form-control'}
                            value={this.state[FIELD_MODEL_GROUP]}
                            onChange={(event) =>
                                this._handleFormChange(
                                    FIELD_MODEL_GROUP,
                                    event.target.value
                                )
                            }
                            data-testid="model-group-select"
                        >
                            {!hasSelectedItem &&
                                renderOption(this.state[FIELD_MODEL_GROUP])}
                            {_.map(modelGroups, renderOption)}
                        </select>
                    </ValidatorFormGroup>
                </Col>
            </Row>
        );
    };

    renderContractualTypeSelect = () => {
        const isAdmin = this.props.isAdminAccountManager;

        return (
            <FieldContractualType
                label={this.props.strings.contractual_type}
                value={this.state[FIELD_CONTRACTUAL_TYPE]}
                onChange={(event) =>
                    this._handleFormChange(
                        FIELD_CONTRACTUAL_TYPE,
                        event.target.value
                    )
                }
                disabled={this.props.disabled || !isAdmin}
            />
        );
    };

    renderBasedOnPartnerManagerDetails = () => {
        if (this.props.partnerManagerDetailsStatus !== RequestStatus.SUCCESS) {
            return [];
        }

        if (this.isExternalManager()) {
            return [
                this.renderDescriptionBox(),
                this.renderDryerWashingMachineInfo(),
                this.renderContractualTypeSelect(),
                this.renderPowerControlSelect(),
                this.renderSerialNumberInput(),
            ];
        } else {
            return [
                this.renderDryerWashingMachineInfo(),
                this.renderModelInformation(),
                this.renderModelGroupDropdown(),
                this.renderDescriptionBox(),
                this.renderSerialNumberInput(),
                this.renderPowerControlSelect(),
                this.renderContractualTypeSelect(),
            ];
        }
    };

    render() {
        const errorMessage = this._getErrorMessage();
        return (
            <Col>
                {this.renderBasedOnPartnerManagerDetails()}
                {errorMessage}
            </Col>
        );
    }
}

const selectSerialNumbers = memoizeOne(
    (connectedSwitchSerialNumbers = [], switchSerialNumbers = []) => {
        const seen = new Set();
        return [
            ...connectedSwitchSerialNumbers.toSorted((a, b) =>
                a.number.localeCompare(b.number)
            ),
            ...switchSerialNumbers.toSorted((a, b) =>
                a.number.localeCompare(b.number)
            ),
        ].filter((item) => {
            const key = item?.number;
            if (!key || seen.has(key)) {
                return false;
            }
            seen.add(key);
            return true;
        });
    }
);

export default connectR(
    (store) => ({
        applianceStore: store.appliance,
        serialNumbers: selectSerialNumbers(
            store.serialNumbers.connectedSwitchSerialNumbers,
            store.serialNumbers.switchSerialNumbers
        ),
        serialNumbersStatus:
            store.pbRequest[PbRequest.GET_CONNECTED_SWITCH_SERIAL_NUMBERS]
                .status,
        locationsByMansionsById: store.locations.locationsByMansionsById,
        applianceDefaults: store.accountingAreaDefaults.applianceDefaults,
        createApplianceRequest: store.pbRequest[PbRequest.CREATE_APPLIANCE],
        updateApplianceRequest: store.pbRequest[PbRequest.UPDATE_APPLIANCE],
        deleteApplianceRequest: store.pbRequest[PbRequest.DELETE_APPLIANCE],
        contractDetails: store.userDetails.contractDetails,
        isAdminAccountManager: store.partnerManager.isAdminAccountManager,
        partnerManagerDetailsStatus:
            store.pbRequest[PbRequest.GET_PARTNER_MANAGER_DETAILS].status,
        partnerManagerDetails: store.partnerManager.partnerManagerDetails,
    }),
    {
        resetApplianceRequestStates,
        MaybeLoadContractDetails,
        LoadPartnerManagerDetails,
        LoadAccountAreaApplianceDefaults,
        LoadConnectedSwitchSerialNumbers,
    }
)(translate('ApplianceBasicForm')(ApplianceBasicForm));
